import { useEffect, useRef, useState, type ReactNode } from "react";
import { isAxiosError } from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog } from "@/components/ui/design-system";
import { DocumentViewer } from "@/components/ui/document-workspace";
import { procitajGreske } from "@/lib/greske";
import {
  equipmentE2Api,
  type E2Document,
  type E2Result,
} from "@/lib/api/oprema-e2";
import { useEquipmentAccess } from "./shared";
import { EquipmentPhotoLink } from "./EquipmentPhotoLink";
import { EquipmentBack } from "./EquipmentNavigation";

export function useE2Save() {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  async function run<T>(task: () => Promise<T>, saved: (value: T) => void) {
    if (busy) return;
    setBusy(true);
    setError("");
    setFields({});
    try {
      saved(await task());
    } catch (cause) {
      const detail: unknown = isAxiosError(cause)
        ? cause.response?.data?.detail
        : undefined;
      if (
        detail &&
        typeof detail === "object" &&
        "poruka" in detail &&
        typeof detail.poruka === "string"
      ) {
        setError(detail.poruka);
        if ("polje" in detail && typeof detail.polje === "string")
          setFields({ [detail.polje]: detail.poruka });
      } else {
        const parsed = procitajGreske(cause);
        setFields(parsed.polja);
        setError(
          [...parsed.opcenito, ...Object.values(parsed.polja)].join(" "),
        );
      }
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, fields, run };
}
export function E2Errors({ save }: { save: ReturnType<typeof useE2Save> }) {
  return save.error ? (
    <p className="equipment-error" role="alert">
      {save.error}
    </p>
  ) : null;
}
export function ProcessState({
  state,
  children,
}: {
  state: "u_tijeku" | "ceka_upravu" | "zavrsena" | "zatvoreno";
  children?: ReactNode;
}) {
  return (
    <div className="equipment-row">
      <Badge
        tone={
          state === "u_tijeku"
            ? "info"
            : state === "ceka_upravu"
              ? "warn"
              : "ok"
        }
      >
        {state === "u_tijeku"
          ? "U tijeku"
          : state === "ceka_upravu"
            ? "Čeka potvrdu uprave"
            : state === "zatvoreno"
              ? "Zatvoreno"
              : "Završeno"}
      </Badge>
      {children}
    </div>
  );
}
export function E2DocumentActions({ document }: { document: E2Document }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const access = useEquipmentAccess(),
    save = useE2Save();
  const [url, setUrl] = useState(""),
    [message, setMessage] = useState("");
  const [attached, setAttached] = useState<{
    documentId: string;
    ids: string[];
  } | null>(null);
  const signed = [
    ...new Set([
      ...(document.potpisani ?? []),
      ...(attached?.documentId === document.id ? attached.ids : []),
    ]),
  ];
  const resource = useRef("");
  const signedInput = useRef<HTMLInputElement>(null);
  const controller = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      controller.current?.abort();
      if (resource.current) URL.revokeObjectURL(resource.current);
    },
    [],
  );
  const close = () => {
    if (location.state?.equipmentDocument === document.id) navigate(-1);
    else {
      const next = new URLSearchParams(params);
      next.delete("dokument");
      navigate(
        { search: next.toString() },
        { replace: true, state: location.state },
      );
    }
  };
  const downloadPdf = () => {
    const download = window.document.createElement("a");
    download.href = url;
    download.download = `${document.broj}.pdf`;
    download.click();
  };
  return (
    <article className="equipment-card">
      <b>{document.broj}</b>
      {(document.bez_potpisa || document.bez_fotke) && (
        <p className="equipment-error">
          {document.bez_potpisa && "Bez potpisa"}
          {document.bez_potpisa && document.bez_fotke && " · "}
          {document.bez_fotke && "Bez fotke"}
        </p>
      )}
      <Button
        variant="outline"
        disabled={save.busy}
        onClick={() =>
          void save.run(
            () => {
              controller.current?.abort();
              controller.current = new AbortController();
              return equipmentE2Api.document(
                document.id,
                controller.current.signal,
              );
            },
            (blob) => {
              if (controller.current?.signal.aborted) return;
              if (resource.current) URL.revokeObjectURL(resource.current);
              resource.current = URL.createObjectURL(blob);
              setUrl(resource.current);
              const next = new URLSearchParams(params);
              next.set("dokument", document.id);
              navigate(
                { search: next.toString() },
                { state: { ...location.state, equipmentDocument: document.id } },
              );
            },
          )
        }
      >
        Otvori / Ispiši
      </Button>
      {access.maintain && (
        <>
          <Button
            type="button"
            variant="outline"
            disabled={save.busy}
            onClick={() => signedInput.current?.click()}
          >
            Priloži potpisani
          </Button>
          <input
            ref={signedInput}
            hidden
            type="file"
            accept="image/jpeg,image/png"
            aria-label={`Priloži potpisani: ${document.broj}`}
            disabled={save.busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file)
                void save.run(
                  () => equipmentE2Api.signed(document.id, file),
                  (result) => {
                    setAttached({
                      documentId: document.id,
                      ids: result.dokument.potpisani ?? [],
                    });
                    setMessage("Potpisani dokument priložen.");
                  },
                );
              e.target.value = "";
            }}
          />
        </>
      )}
      {signed.map((id, index) => (
        <EquipmentPhotoLink
          key={id}
          id={id}
          label={`Potpisani ${document.broj} · ${index + 1}`}
        />
      ))}
      <E2Errors save={save} />
      {message && <p role="status">{message}</p>}
      {url && params.get("dokument") === document.id && (
        <Dialog fullscreen title={document.broj} onClose={close}>
          <EquipmentBack
            onBack={close}
            crumbs={[
              { label: "Zapisnici", to: "/oprema/zapisnici" },
              { label: document.broj },
            ]}
          />
          <div className="equipment-actions">
            <Button
              variant="outline"
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
            >
              Otvori za ispis
            </Button>
            <Button
              variant="outline"
              onClick={downloadPdf}
            >
              Preuzmi PDF
            </Button>
          </div>
          <DocumentViewer
            url={url}
            mime="application/pdf"
            title={document.broj}
            fallbackAction={
              <Button variant="outline" onClick={downloadPdf}>Preuzmi PDF</Button>
            }
          />
        </Dialog>
      )}
    </article>
  );
}
export function E2Notice({
  result,
  message,
  onUndone,
}: {
  result: E2Result | null;
  message: string;
  onUndone: () => void;
}) {
  const [at, setAt] = useState(() => Date.now());
  const save = useE2Save();
  useEffect(() => {
    if (!result) return;
    const timer = window.setInterval(() => setAt(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [result]);
  if (!result) return null;
  const seconds = Math.max(
    0,
    Math.ceil((Date.parse(result.ponistivo_do) - at) / 1000),
  );
  return (
    <section className="equipment-card">
      <p role="status">
        {result.status === "ceka_upravu" ? "Čeka potvrdu uprave." : message}
      </p>
      {seconds > 0 && (
        <Button
          variant="outline"
          disabled={save.busy}
          onClick={() =>
            void save.run(() => equipmentE2Api.undo(result.id), onUndone)
          }
        >
          Poništi ({seconds} s)
        </Button>
      )}
      <E2Errors save={save} />
      <E2DocumentActions document={result.dokument} />
    </section>
  );
}
